import { useState } from 'react'
import { Select, type SelectProps } from '@grovus/design-system'
import { axisCopy, DICTIONARY_COPY, type Lang } from '../i18n'
import { fill } from './format'
import { matchesOffered, searchMatches, valueLabel, type Axis, type HypothesisValue } from '../hypotheses/dictionaryApi'

/** The picker item that creates the typed name; no value id can take this form. */
const CREATE = '__create__'
/** The picker item that clears the axis; no value id can take this form either. */
const CLEAR = '__clear__'

type Passed = Omit<
  SelectProps,
  'data' | 'value' | 'onChange' | 'searchable' | 'searchValue' | 'onSearchChange' | 'filter'
>

interface Props extends Passed {
  lang: Lang
  /** Names the create item: «Создать идею» or «Создать визуал». */
  axis: Axis
  /** The assignable values of the axis. */
  values: HypothesisValue[]
  value: string | null
  /** A value was chosen, or the choice was cleared with `null`. */
  onChoose: (id: string | null) => void
  /** "Create" was chosen for this trimmed name (GRO-158). */
  onCreate: (name: string) => void
  /** The label of a clear item at the top; without it the picker offers none. */
  clearLabel?: string
}

/**
 * The searchable dictionary picker of the assign bar (GRO-157) and the chips cell
 * (GRO-170): the axis's values, found by any of their three labels, a "Create" item for a
 * typed name that no offered value carries, and an optional clear item.
 */
export function DictionaryPicker({ lang, axis, values, value, onChoose, onCreate, clearLabel, ...rest }: Props) {
  const copy = axisCopy(DICTIONARY_COPY[lang], axis)
  const [search, setSearch] = useState('')
  const typed = search.trim()
  const creatable = typed !== '' && !matchesOffered(typed, values)
  const byId = new Map(values.map((candidate) => [candidate.id, candidate]))
  // A value is found by any of its three labels, so a name typed in another language still
  // reaches it; the create and clear items stay whatever the search.
  const filter: SelectProps['filter'] = ({ options, search: text }) =>
    options.filter((option) => {
      if (!('value' in option) || option.value === CREATE || option.value === CLEAR) return true
      const candidate = byId.get(option.value)
      return candidate !== undefined && searchMatches(text, candidate)
    })
  const choose = (next: string | null) => {
    if (next === CLEAR) {
      setSearch('')
      onChoose(null)
      return
    }
    if (next !== CREATE) {
      onChoose(next)
      return
    }
    setSearch('')
    if (typed !== '') onCreate(typed)
  }

  return (
    <Select
      {...rest}
      searchable
      data={[
        ...(clearLabel !== undefined ? [{ value: CLEAR, label: clearLabel }] : []),
        ...values.map((candidate) => ({ value: candidate.id, label: valueLabel(candidate, lang) })),
        ...(creatable ? [{ value: CREATE, label: fill(copy.createOption, { name: typed }) }] : []),
      ]}
      value={value}
      onChange={choose}
      searchValue={search}
      onSearchChange={setSearch}
      filter={filter}
    />
  )
}
